import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  onSnapshot,
  serverTimestamp,
  where,
  doc,
  updateDoc,
  increment,
  runTransaction,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { colAt, withBase } from "./fbPath";
import "./css/Discussion.css"; // NEW: separate CSS
import { toast } from "react-toastify"; // ✅ import toast
import { UserName, UserAvatar } from "../../../context/profileUI";

export default function Discussion({ basePath, authorId }) {
  const auth = getAuth();
  const user = auth.currentUser;

  // form state
  const [mode, setMode] = useState("comment"); // "comment" | "question"
  const [commentText, setCommentText] = useState("");
  const [qTitle, setQTitle] = useState("");
  const [qBody, setQBody] = useState("");

  // list state
  const [filter, setFilter] = useState("all"); // "all" | "comment" | "question"
  const [threads, setThreads] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [commentCount, setCommentCount] = useState(0);
  const threadsCol = useMemo(
    () => (basePath?.length ? colAt(...withBase(basePath, "threads")) : null),
    [JSON.stringify(basePath)]
  );

  useEffect(() => {
    if (!basePath?.length) return;
    const missionDocRef = threadsCol?.parent;
    return onSnapshot(missionDocRef, (snap) => {
      if (snap.exists()) {
        setCommentCount(snap.data().commentCount || 0);
      }
    });
  }, [JSON.stringify(basePath)]);

  // realtime first page
  useEffect(() => {
    if (!threadsCol) return;
    let q1;
    if (filter === "all") {
      q1 = query(threadsCol, orderBy("createdAt", "desc"), limit(20));
    } else {
      q1 = query(
        threadsCol,
        where("type", "==", filter),
        orderBy("createdAt", "desc"),
        limit(20)
      );
    }
    const unsub = onSnapshot(q1, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setThreads(docs);
      setCursor(docs[docs.length - 1] ?? null);
      setHasMore(docs.length === 20);
    });
    return () => unsub();
  }, [threadsCol, filter]);

  const loadMore = useCallback(async () => {
    if (!threadsCol || !cursor) return;
    let q2;
    if (filter === "all") {
      q2 = query(
        threadsCol,
        orderBy("createdAt", "desc"),
        startAfter(cursor.createdAt || cursor),
        limit(20)
      );
    } else {
      q2 = query(
        threadsCol,
        where("type", "==", filter),
        orderBy("createdAt", "desc"),
        startAfter(cursor.createdAt || cursor),
        limit(20)
      );
    }
    const snap = await getDocs(q2);
    const more = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setThreads((prev) => [...prev, ...more]);
    setCursor(more[more.length - 1] ?? null);
    setHasMore(more.length === 20);
  }, [threadsCol, cursor, filter]);

  // submit thread (comment or question)
  const submit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!user) {
        toast.info("Please login to comment");
        return;
      }

      // DocumentReference to the parent mission (the discussion container)
      const missionDocRef = threadsCol?.parent; // <- key line

      if (mode === "comment") {
        const text = commentText.trim();
        if (!text) return;

        const temp = {
          id: "temp-" + Date.now(),
          type: "comment",
          text,
          userId: user.uid,
          createdAt: { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 },
          repliesCount: 0,
          status: "n/a",
        };
        setThreads((prev) => [temp, ...prev]);
        setCommentText("");

        try {
          await addDoc(threadsCol, {
            type: "comment",
            text,
            userId: user.uid,
            createdAt: serverTimestamp(),
            repliesCount: 0,
            status: "n/a",
          });

          // ✅ increment global count (comments + questions)
          if (missionDocRef) {
  await updateDoc(missionDocRef, { commentCount: increment(1) });
}

        } catch {
          setThreads((prev) => prev.filter((t) => t.id !== temp.id));
          setCommentText(text);
          toast.error("Failed to post comment");
        }
      } else {
        // question
        const title = qTitle.trim();
        const body = qBody.trim();
        if (!title) return;

        const temp = {
          id: "temp-" + Date.now(),
          type: "question",
          title,
          body,
          userId: user.uid,
          createdAt: { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 },
          repliesCount: 0,
          status: "open",
        };
        setThreads((prev) => [temp, ...prev]);
        setQTitle("");
        setQBody("");

        try {
          await addDoc(threadsCol, {
            type: "question",
            title,
            body,
            userId: user.uid,
            createdAt: serverTimestamp(),
            repliesCount: 0,
            status: "open",
          });

          // ✅ increment global count (comments + questions)
          if (missionDocRef) {
  await updateDoc(missionDocRef, { commentCount: increment(1) });
}

        } catch {
          setThreads((prev) => prev.filter((t) => t.id !== temp.id));
          setQTitle(title);
          setQBody(body);
          toast.error("Failed to post question");
        }
      }
    },
    [mode, commentText, qTitle, qBody, threadsCol, user?.uid]
  );

  // compute empty-state just for Comments tab request
  const showCommentsEmpty = filter === "comment" && threads.length === 0;

  return (
    <section className="msn-discussion">
      {/* Head + tabs */}
      <div className="msn-discussion__head">
        <h2 className="msn-h2">Discussion</h2>
        <div
          className="msn-discussion__tabs"
          role="tablist"
          aria-label="Filter posts"
        >
          <button
            className="msn-discussion__tab"
            onClick={() => setFilter("all")}
            aria-pressed={filter === "all"}
          >
            All
          </button>
          <button
            className="msn-discussion__tab"
            onClick={() => setFilter("comment")}
            aria-pressed={filter === "comment"}
          >
            Comments
          </button>
          <button
            className="msn-discussion__tab"
            onClick={() => setFilter("question")}
            aria-pressed={filter === "question"}
          >
            Questions
          </button>
        </div>
      </div>

      {/* Threads list */}
      <div className="msn-discussion__listWrap">
        {showCommentsEmpty ? (
          <div className="msn-discussion__empty">
            No comments — <strong>be the first to comment.</strong>
          </div>
        ) : (
          <>
            <ul className="msn-discussion__list">
              {threads.map((t) => (
                <li key={t.id} className="msn-thread">
                  <details>
                    <summary>
                      <span className="msn-thread__type">
                        {t.type === "question" ? "❓" : "💬"}
                      </span>
                      <strong style={{ marginRight: 6 }}>
                        {t.type === "question"
                          ? t.title || "(no title)"
                          : t.text?.slice(0, 120) || ""}
                      </strong>
                      <span className="msn-thread__meta">
                        · <UserName userId={t.userId} />
                        {t.userId === authorId && (
                          <span title="Mission Author"> 👑</span>
                        )}
                        ·{" "}
                        {t.createdAt?.seconds
                          ? new Date(
                              t.createdAt.seconds * 1000
                            ).toLocaleString()
                          : "just now"}
                        {t.type === "question" && ` · ${t.status || "open"}`}
                        {typeof t.repliesCount === "number" &&
                          ` · ${t.repliesCount} replies`}
                      </span>
                    </summary>

                    {t.type === "question" && t.body && (
                      <p style={{ marginTop: 8 }}>{t.body}</p>
                    )}
                    {t.type === "comment" && t.text && (
                      <p style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                        {t.text}
                      </p>
                    )}

                    <ThreadReplies
                      basePath={[...withBase(basePath, "threads", t.id)]}
                      threadId={t.id}
                      authorId={authorId}
                    />
                  </details>
                </li>
              ))}
            </ul>

            {hasMore && (
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <button className="msn-btn" onClick={loadMore}>
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Composer (moved BELOW the list) */}
      <form onSubmit={submit} className="msn-composer">
        {!user ? (
          <textarea
            className="msn-textarea"
            placeholder="Please login to comment"
            rows={3}
            disabled
          />
        ) : (
          <>
            <div className="msn-composer__modes">
              <label className="msn-composer__mode">
                <input
                  type="radio"
                  name="mode"
                  value="comment"
                  checked={mode === "comment"}
                  onChange={() => setMode("comment")}
                />
                <span style={{ marginLeft: 6 }}>Comment</span>
              </label>
              <label className="msn-composer__mode">
                <input
                  type="radio"
                  name="mode"
                  value="question"
                  checked={mode === "question"}
                  onChange={() => setMode("question")}
                />
                <span style={{ marginLeft: 6 }}>Question</span>
              </label>
            </div>

            {mode === "comment" ? (
              <>
                <textarea
                  className="msn-textarea"
                  placeholder="Share a tip or thought…"
                  rows={3}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  maxLength={2000}
                  aria-label="Write a comment"
                />
                <div className="msn-composer__footer">
                  <small className="msn-counter">
                    {commentText.length}/2000
                  </small>
                  <button className="msn-btn msn-btn--primary" type="submit">
                    Post
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  className="msn-input"
                  placeholder="Question title (e.g., Best stealth route?)"
                  value={qTitle}
                  onChange={(e) => setQTitle(e.target.value)}
                  maxLength={140}
                  aria-label="Question title"
                />
                <textarea
                  className="msn-textarea"
                  placeholder="Add details (optional)…"
                  rows={3}
                  value={qBody}
                  onChange={(e) => setQBody(e.target.value)}
                  maxLength={4000}
                  aria-label="Question details"
                />
                <div style={{ display: "flex", justifyContent: "end" }}>
                  <button className="msn-btn msn-btn--primary" type="submit">
                    Ask
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </form>
    </section>
  );
}

function ThreadReplies({ basePath, threadId, authorId }) {
  const auth = getAuth();
  const user = auth.currentUser;
  const [answers, setAnswers] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    const repliesCol = colAt(...withBase(basePath, "replies"));
    const q = query(repliesCol, orderBy("createdAt", "asc"), limit(50));
    return onSnapshot(q, (snap) => {
      setAnswers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [JSON.stringify(basePath)]);

  const submit = useCallback(async () => {
    if (!user) return alert("Please sign in");
    const body = text.trim();
    if (!body) return;
    await addDoc(colAt(...withBase(basePath, "replies")), {
      body,
      userId: user.uid,
      createdAt: serverTimestamp(),
      isAccepted: false,
    });
    // atomically increment repliesCount on the parent thread (transaction)
    try {
      const parentThreadsCol = colAt(
        ...withBase(basePath.slice(0, -2), "threads")
      );
      const threadRef = doc(parentThreadsCol, threadId);
      await runTransaction(parentThreadsCol.firestore, async (tx) => {
        const snap = await tx.get(threadRef);
        const current = (snap.exists() ? snap.data().repliesCount : 0) || 0;
        tx.update(threadRef, { repliesCount: current + 1 });
      });
    } catch (e) {
      console.error("Failed to increment repliesCount:", e);
      toast.warn("Reply posted, but couldn’t update the counter.");
    }

    setText("");
  }, [JSON.stringify(basePath), user?.uid, text]);

  return (
    <div className="msn-replies">
      <ul className="msn-discussion__list" style={{ gap: 10 }}>
        {answers.map((a) => (
          <li key={a.id} className="msn-reply">
            <UserAvatar userId={a.userId} />
            <div style={{ flex: 1 }}>
              <div className="msn-reply__meta">
                <strong>
                  <UserName userId={a.userId} />
                  {a.userId === authorId && (
                    <span title="Mission Author"> 👑</span>
                  )}
                </strong>
                <span className="msn-reply__muted">
                  {a.createdAt?.seconds
                    ? new Date(a.createdAt.seconds * 1000).toLocaleString()
                    : ""}
                </span>
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{a.body}</div>
            </div>
          </li>
        ))}
      </ul>

      <div className="msn-reply__composer">
        <input
          className="msn-input"
          placeholder="Write a reply…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button className="msn-btn" onClick={submit}>
          Reply
        </button>
      </div>
    </div>
  );
}
